import { useState } from "react";
import { toast } from "sonner";
import type {
  ClaimResponse,
  ClaimStrategyResponse,
} from "@patent-studio/api-types";
import { Button } from "@/components/ui/button";
import { ConfirmDialog } from "@/components/ui/ConfirmDialog";
import { Menu, MenuGroup, MenuItem, MenuSeparator } from "@/components/ui/Menu";
import {
  useDemoteClaim,
  useEditClaim,
  usePromoteClaim,
  useRemoveClaim,
} from "@/hooks/use-claim-strategy";
import { plural } from "@/lib/plural";
import { Refusal } from "./ChainControls";
import { latestRefusal } from "./latest-refusal";

/** What marking a claim as a placeholder does, said the same way wherever it is read. */
export const PLACEHOLDER_HELD =
  "Keeps the claim in the draft, flagged as not yet settled. It can't be confirmed until cleared.";
const CLEAR = "Treat this claim as settled again. It can then be confirmed.";
const SAME_CATEGORY =
  "Only claims in its own category are offered: a claim and the claim it depends on share a category.";
const TO_ANOTHER_INDEPENDENT =
  "Change its category on its tag to reach the others.";
const TO_ANOTHER_DEPENDENT =
  "To move it to another category, promote it first, then change its category on its tag.";

/** Every claim beneath the one named, however deep. */
export function claimsBeneath(
  claimId: string,
  stage: ClaimStrategyResponse,
): Set<string> {
  const found = new Set<string>();
  const frontier = [claimId];
  for (let at = frontier.pop(); at !== undefined; at = frontier.pop()) {
    for (const claim of stage.claims) {
      if (claim.parent_claim_id === at && !found.has(claim.claim_id)) {
        found.add(claim.claim_id);
        frontier.push(claim.claim_id);
      }
    }
  }
  return found;
}

/** What removing a claim takes with it, in the words the confirmation reads. */
function removal(claim: ClaimResponse, stage: ClaimStrategyResponse): string {
  const dependents = claimsBeneath(claim.claim_id, stage).size;
  const chain =
    dependents === 0
      ? ""
      : `Its ${plural(dependents, "dependent claim")} ${dependents === 1 ? "goes" : "go"} with it. `;
  return `${chain}The positions it rests on return to the positions not yet placed.`;
}

/**
 * The claims a claim can be made to depend on: any other claim of its category
 * that is not beneath it and not already its parent. Its category is changed
 * on its tag.
 */
function parentsFor(claim: ClaimResponse, stage: ClaimStrategyResponse) {
  const beneath = claimsBeneath(claim.claim_id, stage);
  return stage.claims.filter(
    (c) =>
      c.form_id === claim.form_id &&
      c.claim_id !== claim.claim_id &&
      c.claim_id !== claim.parent_claim_id &&
      !beneath.has(c.claim_id),
  );
}

/** Mark or clear the placeholder, and say which once the write lands. */
function markPlaceholder(
  edit: ReturnType<typeof useEditClaim>,
  claimId: string,
  placeholder: boolean,
) {
  edit.mutate(
    { claimId, body: { placeholder } },
    {
      onSuccess: () => {
        toast(
          placeholder
            ? "Marked as a placeholder, not yet final."
            : "Placeholder cleared.",
        );
      },
    },
  );
}

/**
 * Where the claim can go: each claim it could depend on.
 *
 * A child of the menu rather than part of it, because `Menu` mounts its
 * children only while it is open. Read in the menu's body, every closed claim
 * in the set would walk the whole set and build an item for each destination on
 * every render, and a drag re-renders the set once per row the pointer crosses.
 */
function Destinations({
  claim,
  stage,
  onDemote,
}: {
  claim: ClaimResponse;
  stage: ClaimStrategyResponse;
  onDemote: (parentClaimId: string) => void;
}) {
  const parents = parentsFor(claim, stage).map((parent) => (
    <MenuItem
      key={parent.claim_id}
      title={
        claim.kind === "independent"
          ? `Make dependent of claim ${String(parent.number)}`
          : `Move under claim ${String(parent.number)}`
      }
      onSelect={() => {
        onDemote(parent.claim_id);
      }}
    />
  ));
  // A claim and its parent share a category, so the claims of another category are never
  // offered; the menu says why rather than leave the attorney to wonder where they went.
  if (!stage.claims.some((c) => c.form_id !== claim.form_id)) return parents;
  return (
    <MenuGroup
      heading={
        claim.kind === "independent" ? "Make dependent" : "Move under a claim"
      }
      description={`${SAME_CATEGORY} ${claim.kind === "independent" ? TO_ANOTHER_INDEPENDENT : TO_ANOTHER_DEPENDENT}`}
    >
      {parents}
    </MenuGroup>
  );
}

/**
 * The acts that reshape one claim, in the design's menu: mark or clear it as a
 * placeholder, promote a dependent, or make a claim depend on another claim of
 * its category. A claim's category is changed on its tag. A refusal stays on
 * screen in the server's words once the menu has closed.
 */
export function ClaimOptions({
  taskId,
  claim,
  stage,
  onEditText,
}: {
  taskId: string;
  claim: ClaimResponse;
  stage: ClaimStrategyResponse;
  /** Put the caret in the claim's words, which are written in where they are drawn. */
  onEditText: () => void;
}) {
  const edit = useEditClaim(taskId);
  const promote = usePromoteClaim(taskId);
  const demote = useDemoteClaim(taskId);
  const { refused, starting } = latestRefusal([edit, promote, demote]);
  const id = claim.claim_id;
  const placeholder = !claim.placeholder;
  return (
    <>
      <Menu name={`Options for claim ${String(claim.number)}`}>
        <MenuItem
          title="Edit text"
          description="Puts the cursor in the claim's words. Write in them where they are."
          onSelect={onEditText}
        />
        <MenuItem
          title={
            claim.placeholder ? "Clear placeholder" : "Mark as placeholder"
          }
          description={claim.placeholder ? CLEAR : PLACEHOLDER_HELD}
          onSelect={() => {
            starting(edit, () => {
              markPlaceholder(edit, id, placeholder);
            });
          }}
        />
        <MenuSeparator />
        {claim.kind === "dependent" && (
          <MenuItem
            title="Promote to independent"
            description="Makes this claim an independent claim of its form, its words unchanged."
            onSelect={() => {
              starting(promote, () => {
                promote.mutate(id);
              });
            }}
          />
        )}
        <Destinations
          claim={claim}
          stage={stage}
          onDemote={(parentClaimId) => {
            starting(demote, () => {
              demote.mutate({ claimId: id, parentClaimId });
            });
          }}
        />
      </Menu>
      {refused !== undefined && <Refusal error={refused.error} />}
    </>
  );
}

/**
 * Remove a claim after asking, naming what goes with it. Once it is gone, says
 * which positions came back to the tray, so the tray can show them.
 */
export function RemoveClaim({
  taskId,
  claim,
  stage,
  onReturned,
}: {
  taskId: string;
  claim: ClaimResponse;
  stage: ClaimStrategyResponse;
  onReturned: (positionIds: string[]) => void;
}) {
  const [asking, setAsking] = useState(false);
  const remove = useRemoveClaim(taskId);
  return (
    <>
      <Button
        type="button"
        variant="secondary"
        size="dense"
        disabled={remove.isPending}
        title="Remove this claim and return its positions to the unplaced list"
        onClick={() => {
          setAsking(true);
        }}
      >
        Remove
      </Button>
      <ConfirmDialog
        open={asking}
        title={`Remove claim ${String(claim.number)}?`}
        description={removal(claim, stage)}
        actionLabel="Remove"
        destructive
        onCancel={() => {
          setAsking(false);
        }}
        onConfirm={() => {
          setAsking(false);
          // What was waiting before the write, to tell the positions this
          // removal returns from the ones already there.
          const before = new Set(stage.unplaced.map((p) => p.position_id));
          remove.mutate(claim.claim_id, {
            onSuccess: (served) => {
              const back = served.unplaced
                .map((p) => p.position_id)
                .filter((positionId) => !before.has(positionId));
              onReturned(back);
              toast(
                back.length === 1
                  ? "Position returned to the unplaced list."
                  : `${String(back.length)} positions returned to the unplaced list.`,
              );
            },
          });
        }}
      />
      {remove.isError && <Refusal error={remove.error} />}
    </>
  );
}
